import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "../i18n/useTranslation";

const STORAGE_KEY = "mysipur_a11y_prefs";

type Prefs = { textLevel: 0 | 1 | 2; highContrast: boolean };

function readPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { textLevel: 0, highContrast: false };
    const p = JSON.parse(raw) as Partial<Prefs>;
    const textLevel =
      p.textLevel === 1 || p.textLevel === 2 ? p.textLevel : 0;
    return { textLevel, highContrast: Boolean(p.highContrast) };
  } catch {
    return { textLevel: 0, highContrast: false };
  }
}

function applyPrefs(prefs: Prefs) {
  const root = document.documentElement;
  root.dataset.a11yText = String(prefs.textLevel);
  root.classList.toggle("a11y-high-contrast", prefs.highContrast);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const AccessibilityWidget: React.FC = () => {
  const { t } = useTranslation();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [widgetHidden, setWidgetHidden] = useState(false);
  const [prefs, setPrefs] = useState<Prefs>(() => readPrefs());
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    applyPrefs(prefs);
  }, [prefs]);

  useEffect(() => {
    const initial = readPrefs();
    applyPrefs(initial);
    setPrefs(initial);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }, []);

  const hideFloatingWidget = useCallback(() => {
    setOpen(false);
    setWidgetHidden(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const focusables = panel
      ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
          (el) => el.offsetParent !== null || el.getClientRects().length > 0
        )
      : [];
    window.setTimeout(() => focusables[0]?.focus(), 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      if (focusables.length === 0) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else if (document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  const setTextLevel = (textLevel: 0 | 1 | 2) => {
    setPrefs((p) => ({ ...p, textLevel }));
  };

  const toggleContrast = () => {
    setPrefs((p) => ({ ...p, highContrast: !p.highContrast }));
  };

  const reset = () => {
    const next: Prefs = { textLevel: 0, highContrast: false };
    setPrefs(next);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    applyPrefs(next);
  };

  if (widgetHidden) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed bottom-4 start-4 z-[90] flex flex-col items-start">
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${panelId}-title`}
          className="pointer-events-auto mb-3 w-[min(100vw-2rem,18rem)] rounded-2xl border border-white/40 bg-white/95 p-4 shadow-card backdrop-blur-md"
        >
          <h2 id={`${panelId}-title`} className="text-lg font-bold text-heading mb-3">
            {t("a11y.widgetTitle")}
          </h2>
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              onClick={() => setTextLevel(1)}
              disabled={prefs.textLevel === 1}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-heading hover:bg-brandBeige disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brandOrange"
            >
              {t("a11y.widgetIncreaseText")} (1)
            </button>
            <button
              type="button"
              onClick={() => setTextLevel(2)}
              disabled={prefs.textLevel === 2}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-heading hover:bg-brandBeige disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brandOrange"
            >
              {t("a11y.widgetIncreaseText")} (2)
            </button>
            <button
              type="button"
              onClick={() => setTextLevel(0)}
              disabled={prefs.textLevel === 0}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-heading hover:bg-brandBeige disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brandOrange"
            >
              {t("a11y.widgetDecreaseText")}
            </button>
          </div>
          <button
            type="button"
            onClick={toggleContrast}
            aria-pressed={prefs.highContrast}
            className="mb-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-heading hover:bg-brandBeige focus:outline-none focus:ring-2 focus:ring-brandOrange"
          >
            {t("a11y.widgetHighContrast")}
            {prefs.highContrast ? " ✓" : ""}
          </button>
          <button
            type="button"
            onClick={reset}
            className="mb-3 w-full rounded-lg border border-brandNavy/30 bg-brandBeige px-3 py-2 text-sm font-semibold text-heading focus:outline-none focus:ring-2 focus:ring-brandOrange"
          >
            {t("a11y.widgetReset")}
          </button>
          <button
            type="button"
            onClick={close}
            className="w-full rounded-lg px-3 py-2 text-sm font-semibold text-body hover:text-heading focus:outline-none focus:ring-2 focus:ring-brandOrange"
          >
            {t("a11y.widgetClose")}
          </button>
        </div>
      )}
      <div className="pointer-events-auto relative">
        <button
          ref={triggerRef}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-brandNavy text-xl text-white shadow-card ring-2 ring-white/50 hover:bg-brandNavy/90 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
          aria-label={t("a11y.widgetToggle")}
        >
          <span aria-hidden>♿</span>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            hideFloatingWidget();
          }}
          className="absolute -top-0.5 -end-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 bg-white text-[13px] font-bold leading-none text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-1"
          aria-label={t("a11y.widgetHideFloating")}
        >
          <span aria-hidden>×</span>
        </button>
      </div>
    </div>
  );
};

export default AccessibilityWidget;
